<template>
  <div class="stock-watch">
    <header id="blog-header">
      <div class="inner">
        <h1 class="page-title">股票监控</h1>
        <span class="page-desc">自选股票行情与估值(美股 · A股 · 港股)</span>
      </div>
    </header>

    <div class="stock-content">
      <div class="stock-toolbar">
        <button @click="loadStocks">刷新</button>
        <router-link class="manage-link" :to="{ name: 'options' }">管理监控清单 →</router-link>
        <span class="model-name" v-if="modelName">评估模型: {{ modelName }}</span>
      </div>

      <div v-if="loading" class="loading">加载中...</div>
      <div v-if="error" class="error-msg">{{ error }}</div>
      <div v-if="!loading && stocks.length === 0" class="empty-hint">
        暂无自选股票。去 <router-link :to="{ name: 'options' }">监控清单</router-link> 勾选后,
        次日分析任务会自动纳入(或手动执行 run_market_analysis)。
      </div>

      <p class="model-hint" v-if="stocks.length > 0">点击行查看完整模型评估过程 · 期权分析仅美股支持(A股/港股为行情+估值监控)</p>

      <table class="stock-table" v-if="stocks.length > 0">
        <thead>
          <tr>
            <th>代码</th><th>名称</th><th>市场</th>
            <th>收盘</th><th>日涨跌</th><th>行情日期</th>
            <th>52周分位</th><th>综合分</th><th>结论</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="v in stocks">
            <tr :key="v.symbol" class="stock-row" @click="toggleExpand(v.symbol)">
              <td class="ticker-cell">{{ v.symbol }}</td>
              <td>{{ v.name }}</td>
              <td>{{ marketOf(v.symbol) }}</td>
              <td>{{ v.close != null ? v.close.toFixed(2) : '-' }}</td>
              <td :class="changeClass(v.change_pct)">
                {{ v.change_pct != null ? (v.change_pct > 0 ? '+' : '') + v.change_pct.toFixed(2) + '%' : '-' }}
              </td>
              <td>{{ v.quote_date || '-' }}</td>
              <td>{{ v.percentile_52w != null ? v.percentile_52w.toFixed(0) + '%' : '-' }}</td>
              <td :class="compositeClass(v)">{{ compositeOf(v) }}</td>
              <td>
                <span :class="'verdict-' + v.verdict">{{ verdictLabel(v.verdict) }}</span>
                <span v-if="v.detail && v.detail.confidence && v.detail.confidence !== '无'" class="confidence">
                  置信{{ v.detail.confidence }}
                </span>
              </td>
            </tr>
            <tr v-if="expandedSymbol === v.symbol && v.detail" :key="v.symbol + '-detail'" class="detail-row">
              <td colspan="9">
                <div class="eval-process">
                  <h4>模型评估过程 — {{ v.name }} ({{ v.symbol }})</h4>
                  <table class="factor-table" v-if="v.detail.factors">
                    <thead>
                      <tr><th>因子</th><th>原始值</th><th>得分(z量纲)</th><th>权重</th><th>贡献</th></tr>
                    </thead>
                    <tbody>
                      <tr v-for="f in v.detail.factors" :key="f.key">
                        <td>{{ f.label }}</td>
                        <td>{{ f.value != null ? f.value : '缺失' }}</td>
                        <td>{{ f.score != null ? f.score.toFixed(2) : '-' }}</td>
                        <td>{{ f.weight.toFixed(2) }}</td>
                        <td>{{ f.score != null ? (f.score * f.weight).toFixed(3) : '-' }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <ol class="eval-steps" v-if="v.detail.steps">
                    <li v-for="(s, si) in v.detail.steps" :key="si">{{ s }}</li>
                  </ol>
                  <p class="eval-warning" v-if="v.detail.warning">⚠ {{ v.detail.warning }}</p>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'stock-watch',
  data: function () {
    return {
      stocks: [],
      expandedSymbol: '',
      loading: false,
      error: ''
    }
  },
  computed: {
    modelName: function () {
      var first = this.stocks.find(function (v) { return v.detail && v.detail.model })
      return first ? first.detail.model : ''
    }
  },
  mounted: function () {
    this.loadStocks()
  },
  methods: {
    loadStocks: function () {
      var self = this
      self.loading = true
      self.error = ''
      var reqUrl = this.$store.state.baseUrl + '/blog/api/market/valuations/?group=stock'
      this.$http.get(reqUrl).then(function (response) {
        var res = JSON.parse(response.bodyText)
        if (res.result === 'success') {
          self.stocks = res.valuations
        } else {
          self.error = '加载失败'
        }
        self.loading = false
      }).catch(function (err) {
        self.error = '请求失败: ' + err.statusText
        self.loading = false
      })
    },

    marketOf: function (symbol) {
      if (/\.(SS|SZ|BJ)$/.test(symbol)) return 'A股'
      if (/\.HK$/.test(symbol)) return '港股'
      return '美股'
    },

    changeClass: function (pct) {
      if (pct == null) return ''
      return pct > 0 ? 'chg-up' : (pct < 0 ? 'chg-down' : '')
    },

    toggleExpand: function (symbol) {
      this.expandedSymbol = this.expandedSymbol === symbol ? '' : symbol
    },

    compositeOf: function (v) {
      if (!v.detail || v.detail.composite == null) return '-'
      var c = v.detail.composite
      return (c > 0 ? '+' : '') + c.toFixed(2)
    },

    compositeClass: function (v) {
      if (!v.detail || v.detail.composite == null) return ''
      return Math.abs(v.detail.composite) >= (v.detail.threshold || 1.0) ? 'composite-active' : ''
    },

    verdictLabel: function (verdict) {
      return { undervalued: '低估', overvalued: '高估', neutral: '中性' }[verdict] || verdict
    }
  }
}
</script>

<style scoped>
.stock-watch {
  min-height: 100vh;
}

#blog-header {
  padding: 3rem 1rem 2rem;
  text-align: center;
}

.page-title {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.page-desc {
  color: #888;
}

.stock-content {
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 1rem 3rem;
}

.stock-toolbar {
  display: flex;
  gap: 14px;
  align-items: center;
  margin-bottom: 12px;
}

.stock-toolbar button {
  padding: 6px 16px;
}

.manage-link {
  font-size: 14px;
  color: #3273dc;
}

.model-name {
  font-size: 13px;
  color: #888;
  font-family: monospace;
  margin-left: auto;
}

.model-hint {
  font-size: 12px;
  color: #aaa;
  margin: 4px 0 8px;
}

.loading, .error-msg, .empty-hint {
  padding: 1rem 0;
}

.error-msg { color: #c62828; }

.empty-hint { color: #888; font-size: 14px; }

.stock-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.stock-table th, .stock-table td {
  border-bottom: 1px solid #eee;
  padding: 6px 8px;
  text-align: left;
}

.ticker-cell {
  font-family: monospace;
  font-weight: bold;
}

.stock-row {
  cursor: pointer;
}

.stock-row:hover {
  background: #f5f8ff;
}

.chg-up { color: #c62828; }
.chg-down { color: #2e7d32; }

.composite-active { font-weight: bold; }

.confidence {
  font-size: 11px;
  color: #999;
  margin-left: 4px;
}

.verdict-undervalued { color: #2e7d32; font-weight: bold; }
.verdict-overvalued { color: #c62828; font-weight: bold; }
.verdict-neutral { color: #999; }

.detail-row td {
  background: #fafcff;
  padding: 12px 16px;
}

.eval-process h4 {
  margin: 0 0 8px;
  font-size: 14px;
}

.factor-table {
  width: auto;
  min-width: 60%;
  border-collapse: collapse;
  font-size: 12px;
  margin-bottom: 10px;
}

.factor-table th, .factor-table td {
  border: 1px solid #e0e8f0;
  padding: 4px 10px;
  text-align: right;
}

.factor-table th:first-child, .factor-table td:first-child {
  text-align: left;
}

.eval-steps {
  font-size: 12px;
  color: #555;
  line-height: 1.8;
  padding-left: 18px;
  margin: 0;
}

.eval-warning {
  color: #c62828;
  font-size: 12px;
  margin: 8px 0 0;
}
</style>
